import { useState } from "react";
import type { VenueClient, VenueMatchResult, VenueMenu, VenuePreferences } from "@vibetail/contracts";
import { MatchFlow } from "../../matching/components/MatchFlow.js";
import { SiteFooter, SiteHeader } from "../../platform/components/SiteHeader.js";
import { useSeo } from "../../platform/useSeo.js";
import { LangContext, type Lang } from "../../../lib/i18n.js";

interface VenueExperienceProps {
  client: VenueClient;
  initialPreferences?: VenuePreferences;
  initialResult?: VenueMatchResult;
  menu: VenueMenu;
}

export function VenueExperience({ client, initialPreferences, initialResult, menu }: VenueExperienceProps) {
  const [locale, setLocale] = useState<Lang>("en");
  useSeo(`${menu.venue.name} · ${menu.name} — Vibetail`, menu.shortIntro ?? menu.venue.shortIntro ?? "Match your mood to this live menu.");

  const noVisibleItems = menu.items.length === 0;
  const noActiveItems = !noVisibleItems && menu.items.every((item) => item.availabilityStatus !== "active");
  if (noVisibleItems || noActiveItems) return <UnavailableMenu menu={menu} locale={locale} onLocale={() => setLocale((v) => v === "en" ? "zh" : "en")} />;

  return <LangContext.Provider value={locale}><div className="vt-page"><SiteHeader /><main className="vt-match-main">
    <MatchFlow
      context={{
        kicker: `${menu.venue.name} · ${menu.name}`,
        title: locale === "zh" ? "从这张菜单找到今晚这一杯" : "Match your vibe from this menu",
        description: locale === "zh" ? "告诉我们此刻的状态。匹配只会使用这家酒吧今晚真实可点的项目。" : "Tell us how tonight feels. This match only considers items this bar can actually serve from this menu.",
      }}
      headerAction={<button className="vt-locale-toggle" type="button" onClick={() => setLocale((v) => v === "en" ? "zh" : "en")}>{locale === "en" ? "中文" : "EN"}</button>}
      onLocaleToggle={() => setLocale((v) => v === "en" ? "zh" : "en")}
      {...(initialPreferences ? { initialPreferences } : {})}
      {...(initialResult ? { initialResult } : {})}
      match={(preferences, lang) => client.matchItem(menu.venue.slug, menu.slug, preferences, lang)}
      menuItems={menu.items}
    />
  </main><SiteFooter /></div></LangContext.Provider>;
}

function UnavailableMenu({ locale, menu, onLocale }: { locale: Lang; menu: VenueMenu; onLocale(): void }) {
  const empty = menu.items.length === 0;
  return <div className="vt-page"><SiteHeader /><main className="vt-narrow"><header className="vt-page-title vt-match-title"><div><p className="vt-kicker">{menu.venue.name} · {menu.name}</p><button className="vt-locale-toggle" type="button" onClick={onLocale}>{locale === "en" ? "中文" : "EN"}</button></div><h1>{empty ? (locale === "zh" ? "菜单还是空的" : "This menu is empty") : (locale === "zh" ? "目前没有可点项目" : "Nothing is available right now")}</h1><p>{locale === "zh" ? "请稍后再来，或向酒吧询问今晚的菜单。" : "Please check back later or ask the bar about tonight's menu."}</p></header></main><SiteFooter /></div>;
}
